import React from "react";
import { Body, Card, CardBody, Checkbox, NewBadge } from "@panwds/react-ui";
import { ADVISORY_TYPE_OPTIONS, isAdvisoriesNewBadgeVisible } from "../../advisories/utils/advisoryType";
import { BORDERED_CARD_CLASS } from "../utils/cardStyles";

const ALL_ADVISORY_TYPES = ADVISORY_TYPE_OPTIONS.map(({ value }) => value);

// Every Subscription Types card keeps the same neutral border regardless of selection:
// the checkbox is already an unambiguous selected-state indicator, so recolouring the
// border to the blue accent was redundant emphasis and made the group read as noisy when
// some cards were checked and others were not.

/**
 * Global notification-type selection for the manage-subscriptions modal. These apply
 * across every product and region selected below, so the state lives alongside
 * `checkedProducts` in StatusPage rather than per-product.
 *
 * Advisories has no parent on/off flag on the wire -- `advisoryTypes` is always sent,
 * and an empty array is itself the "opted out of advisories" signal. The parent
 * "Advisories" checkbox below is therefore pure UI sugar for select-all/select-none over
 * the four sub-types; it carries no state of its own and the sub-type checkboxes always
 * stay enabled (there is nothing to gate them on).
 */
const SubscriptionTypesSection = ({ subscriptionTypes, setSubscriptionTypes }) => {
    const setType = (key, checked) => {
        setSubscriptionTypes((prev) => ({ ...prev, [key]: checked }));
    };

    const handleAdvisoryTypeChange = (value, checked) => {
        setSubscriptionTypes((prev) => {
            const current = prev.advisoryTypes || [];
            return {
                ...prev,
                advisoryTypes: checked
                    ? [...current, value]
                    : current.filter((t) => t !== value),
            };
        });
    };

    const handleAdvisoriesToggle = (checked) => {
        setSubscriptionTypes((prev) => ({
            ...prev,
            advisoryTypes: checked ? [...ALL_ADVISORY_TYPES] : [],
        }));
    };

    const selectedAdvisoryTypes = subscriptionTypes.advisoryTypes || [];
    const allAdvisoryTypesSelected = selectedAdvisoryTypes.length === ALL_ADVISORY_TYPES.length;
    const someAdvisoryTypesSelected = selectedAdvisoryTypes.length > 0 && !allAdvisoryTypesSelected;

    return (
        <div className="ssmutw-mb-6">
            <Body size="sm" as="h3" appearance="secondary" addClassName="ssmutw-font-semibold">
                Subscription Types
            </Body>
            <Body size="sm" appearance="secondary" addClassName="ssmutw-mt-1">
                Applies to every product and region below.
            </Body>

            <div className="ssmutw-mt-3 ssmutw-flex ssmutw-flex-col ssmutw-gap-2">
                <Card shadow={false} addClassName={BORDERED_CARD_CLASS}>
                    <CardBody>
                        <Checkbox
                            checked={!!subscriptionTypes.incident}
                            onChange={(e) => setType("incident", e.target.checked)}
                        >
                            Incidents
                        </Checkbox>
                    </CardBody>
                </Card>

                <Card shadow={false} addClassName={BORDERED_CARD_CLASS}>
                    <CardBody>
                        <Checkbox
                            checked={!!subscriptionTypes.maintenance}
                            onChange={(e) => setType("maintenance", e.target.checked)}
                        >
                            Maintenance
                        </Checkbox>
                    </CardBody>
                </Card>

                <Card shadow={false} addClassName={BORDERED_CARD_CLASS}>
                    <CardBody>
                        <div className="ssmutw-flex ssmutw-items-center ssmutw-gap-2">
                            <Checkbox
                                checked={allAdvisoryTypesSelected}
                                indeterminate={someAdvisoryTypesSelected}
                                onChange={(e) => handleAdvisoriesToggle(e.target.checked)}
                            >
                                Advisories
                            </Checkbox>
                            {isAdvisoriesNewBadgeVisible() && <NewBadge />}
                        </div>

                        <fieldset className="ssmutw-mt-3 ssmutw-ml-6 ssmutw-border-0 ssmutw-p-0">
                            <legend className="ssmutw-sr-only">Advisory types</legend>
                            <div className="ssmutw-flex ssmutw-flex-wrap ssmutw-gap-x-6 ssmutw-gap-y-2">
                                {ADVISORY_TYPE_OPTIONS.map(({ value, label }) => (
                                    <Checkbox
                                        key={value}
                                        checked={selectedAdvisoryTypes.includes(value)}
                                        onChange={(e) => handleAdvisoryTypeChange(value, e.target.checked)}
                                    >
                                        {label}
                                    </Checkbox>
                                ))}
                            </div>
                        </fieldset>
                    </CardBody>
                </Card>
            </div>
        </div>
    );
};

export default React.memo(SubscriptionTypesSection);
